import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Moment } from "moment";
import { map, tap } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { ChartSerieValue } from "src/model/class/general classes/chart-serie-value";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { ChartSerieKind, ChartTimeUM } from "src/components/shared-components/enum/enumChart";
import { chartLoadInitStartAction } from "src/state/actions/chart.action";
import { Observable } from "rxjs";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
/**
 * Load informations about charts and their series
 */
export class ChartService {
  constructor(private _http: HttpClient, private _store: Store) { }

  /**
   *  Initialize public observable list trasmission:
   *  it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(chartLoadInitStartAction());
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.Init();
  }

  /**
   * Get full list of chart
   *
   * @returns
   */
  public getCharts(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_CHART
    );
  }

  /**
   * Get last modifed chart
   *
   * @param chartStore Chart store
   * @returns
   */
  public getChartsByLastModified(
    chartStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string = (chartStore?.lastAPIModified || "");

    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_CHART + queryString
      )
      .pipe(
        map((returnChartStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnChartStore) {
            chartStore = returnChartStore;
          }

          return chartStore;
        })
      );
  }

  /**
   * Get chart granularity
   */
  public getChartGranularity(chartID: number): Promise<any> {
    const returnValue = this._http
      .get<any>(
        environment.httpServerRoot +
        AppSettings.API_URL_CHART +
        "/" +
        chartID +
        "/granularity"
      )
      .toPromise();

    return returnValue;
  }

  /**
   * Retrieve values of chart series on the basis of the address used as chart serie data,
   *  the period loaded and the kind of the chart
   *
   * @param instanceGUID Instance guid Address
   * @param address Address to get
   * @param fromDate start date of data loading
   * @param toDate end date of data loading
   * @param kind chart kind
   * @returns
   */
  public async getChartSeriesValuesByAddress(
    instanceGUID: string,
    address: string,
    fromDate: Moment,
    toDate: Moment,
    kind: ChartSerieKind
  ): Promise<Array<ChartSerieValue>> {
    let fromDateTime = encodeURIComponent(
      fromDate.format(MomentFormats.MOMENT_DATETIME_FORMAT)
    );
    let toDateTime = encodeURIComponent(
      toDate.format(MomentFormats.MOMENT_DATETIME_FORMAT)
    );
    if (kind == ChartSerieKind.PIE) {
      fromDateTime = encodeURIComponent(
        fromDate
          .format(MomentFormats.MOMENT_DATETIME_FORMAT)
          .substring(0, 10) + " 00:00:00"
      );
      toDateTime = encodeURIComponent(
        toDate
          .add(1, "days")
          .format(MomentFormats.MOMENT_DATETIME_FORMAT)
          .substring(0, 10) + " 00:00:00"
      );
    }
    const queryString: string =
      "?instanceguid=" +
      encodeURIComponent(instanceGUID) +
      "&address=" +
      encodeURIComponent(address) +
      "&fromDateTime=" +
      fromDateTime +
      "&toDateTime=" +
      toDateTime +
      "&kind=" +
      (kind as Number);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_CHART +
        "/serie/values" +
        queryString
      )
      .pipe(map((values: GenericApiData) => values.data))
      .toPromise();
  }

  /**
   * Retrieve values of chart series on the basis of the id data process,
   *  the period loaded and the kind of the chart
   *
   * @param idDataProcess ID data proess
   * @param fromDate start date of data loading
   * @param toDate end date of data loading
   * @returns
   */
  public async getChartSeriesValuesByIDDataProcess(
    idDataProcess: number,
    fromDate: Moment,
    toDate: Moment,
    kind: ChartSerieKind
  ): Promise<Array<ChartSerieValue>> {
    const queryString: string =
      "?idDataProcess=" +
      encodeURIComponent(idDataProcess) +
      "&fromDateTime=" +
      encodeURIComponent(
        fromDate.format(MomentFormats.MOMENT_DATETIME_FORMAT)
      ) +
      "&toDateTime=" +
      encodeURIComponent(
        toDate.format(MomentFormats.MOMENT_DATETIME_FORMAT)
      ) +
      "&kind=" +
      (kind as Number);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_CHART +
        "/serie/values" +
        queryString
      )
      .pipe(map((values: GenericApiData) => values.data))
      .toPromise();
  }

  /**
   * With bar chart: if I choose a different granularity from the component this function retrieve
   * the correct energy period addresses on the basis of the new granularity chosen
   * @param chartID ID of the chart that we need to load
   * @param newGranularity new granularity that we need to load
   * @returns
   */
  public reloadChartAddressesOnDifferentGranularity(
    chartID: number,
    newGranularity: ChartTimeUM
  ): Promise<Array<string>> {
    const queryString: string =
      "?chartID=" + chartID + "&granularity=" + (newGranularity as Number);

    return this._http
      .get<Array<string>>(
        environment.httpServerRoot +
        AppSettings.API_URL_CHART_SERIE_CHANGE_GRANULARITY +
        queryString
      )
      .toPromise();
  }
}
